import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Gavel, User, TrendingUp, BarChart3, Sparkles } from 'lucide-react';
import AnalyticsModal from './AnalyticsModal';

const JudgeProsecutorAnalytics = ({ searchResults, filters, isLoading }) => {
  const [selectedAnalytics, setSelectedAnalytics] = useState(null);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analyticsType, setAnalyticsType] = useState(null); // 'judge' or 'prosecutor'

  // Get judges from search results (includes both assigned and potential judges)
  const judges = React.useMemo(() => {
    if (!searchResults || searchResults.length === 0) return [];

    const allJudges = searchResults
      .slice(0, 50) // Limit to first 50 results
      .flatMap((caseData) => caseData.judges || [])
      .filter((judge) => judge && judge.firstName && judge.lastName);

    // Deduplicate by firstName|lastName and limit to reasonable number
    const uniqueJudges = Array.from(
      new Map(
        allJudges.map((judge) => [
          `${judge.firstName}|${judge.lastName}`,
          judge
        ])
      ).values()
    ).slice(0, 5); // Limit to 5 judges to match prosecutors

    return uniqueJudges;
  }, [searchResults]);

  const prosecutors = React.useMemo(() => {
    if (!searchResults || searchResults.length === 0) return [];

    const allProsecutors = searchResults
      .slice(0, 50) // Limit to first 50 results
      .flatMap((caseData) => caseData.prosecutingAttorneys || [])
      .filter(
        (prosecutor) =>
          prosecutor && prosecutor.firstName && prosecutor.lastName
      );

    // Get unique prosecutors by firstName|lastName, limit to 5
    const uniqueProsecutors = Array.from(
      new Map(
        allProsecutors.map((prosecutor) => [
          `${prosecutor.firstName}|${prosecutor.lastName}`,
          prosecutor
        ])
      ).values()
    ).slice(0, 5);

    return uniqueProsecutors;
  }, [searchResults]);

  const handleAnalyticsClick = (person, type) => {
    setSelectedAnalytics(person);
    setAnalyticsType(type);
    setShowAnalyticsModal(true);
  };

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gray-100 rounded">
            <BarChart3 className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Search Insights
            </h3>
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <p className="text-sm text-gray-500">Analyzing results...</p>
            </div>
          </div>
        </div>

        {/* Enhanced loading placeholders that match the actual content structure */}
        <div className="animate-pulse space-y-6">
          {/* Judges section placeholder */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-16"></div>
              <div className="ml-auto w-8 h-5 bg-gray-200 rounded"></div>
            </div>
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-3 rounded border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-24 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-32"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Prosecutors section placeholder */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-20"></div>
              <div className="ml-auto w-8 h-5 bg-gray-200 rounded"></div>
            </div>
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <div key={i} className="p-3 rounded border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-24 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-32"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!searchResults || searchResults.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gray-100 rounded">
            <BarChart3 className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Search Insights
            </h3>
            <p className="text-sm text-gray-500">
              Based on your search results
            </p>
          </div>
        </div>
        <div className="text-center py-8">
          <div className="p-3 bg-gray-100 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-gray-400" />
          </div>
          <p className="text-gray-500 text-sm">
            Start searching to discover judge and prosecutor analytics
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-6 min-h-[600px]">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gray-100 rounded">
            <BarChart3 className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Search Insights
            </h3>
            <p className="text-sm text-gray-500">
              Based on {searchResults.length} result
              {searchResults.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Judges Section */}
        {judges.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-gray-100 rounded">
                <Gavel className="w-4 h-4 text-gray-600" />
              </div>
              <h4 className="text-sm font-medium text-gray-900">Judges</h4>
              <span className="ml-auto text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                {judges.length}
              </span>
            </div>
            <div className="text-xs text-gray-500 mb-2 px-1">
              Sentencing patterns
            </div>
            <div className="space-y-1">
              {judges.map((judge, index) => (
                <button
                  key={`${judge.firstName}|${judge.lastName}`}
                  onClick={() => handleAnalyticsClick(judge, 'judge')}
                  className="w-full text-left p-2 rounded border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center text-gray-700 text-sm font-medium">
                      {judge.firstName[0]}
                      {judge.lastName[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm">
                        {judge.firstName} {judge.lastName}
                      </div>
                      {judge.robingRoomUrl && (
                        <a
                          href={judge.robingRoomUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-xs underline"
                          title="View judge profile on The Robing Room"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Robing Room
                        </a>
                      )}
                    </div>
                    <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <TrendingUp className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Prosecutors Section */}
        {prosecutors.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-gray-100 rounded">
                <User className="w-4 h-4 text-gray-600" />
              </div>
              <h4 className="text-sm font-medium text-gray-900">Prosecutors</h4>
              <span className="ml-auto text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                {prosecutors.length}
              </span>
            </div>
            <div className="text-xs text-gray-500 mb-2 px-1">
              Prosecution patterns
            </div>
            <div className="space-y-1">
              {prosecutors.map((prosecutor, index) => (
                <button
                  key={`${prosecutor.firstName}|${prosecutor.lastName}`}
                  onClick={() => handleAnalyticsClick(prosecutor, 'prosecutor')}
                  className="w-full text-left p-2 rounded border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-7 h-7 bg-gray-200 rounded-full flex items-center justify-center text-gray-700 text-sm font-medium">
                      {prosecutor.firstName[0]}
                      {prosecutor.lastName[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm">
                        {prosecutor.firstName} {prosecutor.lastName}
                      </div>
                    </div>
                    <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <TrendingUp className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* No analytics available */}
        {judges.length === 0 && prosecutors.length === 0 && (
          <div className="text-center py-6">
            <div className="p-3 bg-gray-100 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-500 text-sm">
              No judges or prosecutors found in search results
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
            <span>Live insights from your search</span>
          </div>
        </div>
      </div>

      {/* Analytics Modal */}
      {showAnalyticsModal && selectedAnalytics && (
        <AnalyticsModal
          isOpen={showAnalyticsModal}
          onClose={() => setShowAnalyticsModal(false)}
          person={selectedAnalytics}
          type={analyticsType}
          filters={filters}
        />
      )}
    </>
  );
};

JudgeProsecutorAnalytics.propTypes = {
  searchResults: PropTypes.array,
  filters: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired
};

export default JudgeProsecutorAnalytics;
